import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
// eslint-disable-next-line import/no-unresolved
import '../styles/ExecutiveDashboard.css';

import DashboardHeader from '../components/executive-pulse-dashboard/DashboardHeader.jsx';
import Tabs from '../components/executive-pulse-dashboard/Tabs.jsx';
import KPIGrid from '../components/executive-pulse-dashboard/KPIGrid.jsx';
import Gauge from '../components/executive-pulse-dashboard/Gauge.jsx';
import SectionCard from '../components/executive-pulse-dashboard/SectionCard.jsx';
import DrillDownModal from '../components/executive-pulse-dashboard/DrillDownModal.jsx';
import SummaryStrip from '../components/executive-pulse-dashboard/SummaryStrip.jsx';
import ReportingPeriodSelector from '../components/executive-pulse-dashboard/ReportingPeriodSelector.jsx';
import ReportingSubLinks from '../components/header/ReportingSubLinks.jsx';
import LoadingSpinner from '../components/common/LoadingSpinner.jsx';
import ErrorMessage from '../components/common/ErrorMessage.jsx';
import useExecutivePulse from '../hooks/useExecutivePulse.js';
import { setActiveTab } from '../redux/slices/header-slice.js';
import { resolvePeriodFromSearchParams } from '../utils/executive-pulse-reporting-period.js';
import { averageKpiCompletionPercent } from '../utils/executive-pulse-kpi-value.js';
import resolveExecutivePulseKpiId, {
    findExecutivePulseKpiInDashboard
} from '../utils/resolve-executive-pulse-kpi-id.js';
import EXECUTIVE_PULSE_LOGGABLE_KPI_IDS from '../config/executive-pulse-loggable-kpis.js';
import EXECUTIVE_PULSE_TARGET_EDITABLE_KPI_IDS from '../config/executive-pulse-target-editable-kpis.js';

const PILLARS = ['financial', 'customer', 'process', 'growth'];

export default function ExecutiveDashboard({ portalMode }) {
    const dispatch = useDispatch();
    const [searchParams, setSearchParams] = useSearchParams();
    const { year } = resolvePeriodFromSearchParams(searchParams);
    const [active, setActive] = useState('financial');
    const [selectedKpiId, setSelectedKpiId] = useState(null);

    const { data, loading, forbidden, errorMessage, reload, refresh } =
        useExecutivePulse(year);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        if (portalMode) {
            return undefined;
        }
        dispatch(setActiveTab('Reporting'));
        return () => dispatch(setActiveTab(null));
    }, [dispatch, portalMode]);

    const reportingPeriod = data?.meta?.reportingPeriod;
    const items = data?.[active] || [];
    const allItems = useMemo(
        () => PILLARS.flatMap((pillar) => data?.[pillar] || []),
        [data]
    );
    const tabPercent = averageKpiCompletionPercent(items);
    const overallPercent = averageKpiCompletionPercent(allItems);
    const selectedKPI = useMemo(
        () => findExecutivePulseKpiInDashboard(data, selectedKpiId),
        [data, selectedKpiId]
    );

    const onYearChange = (nextYear) => {
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                next.set('year', String(nextYear));
                return next;
            },
            { replace: true }
        );
    };

    const pageClass = `exec-pulse-page${
        portalMode ? ' exec-pulse-page--portal' : ''
    }`;

    const wrapPage = (inner) => (
        <>
            {!portalMode ? <ReportingSubLinks /> : null}
            <div className={pageClass}>{inner}</div>
        </>
    );

    if (forbidden) {
        return wrapPage(
            <div className="exec-pulse-dashboard exec-pulse-dashboard--message">
                <ErrorMessage message="You do not have permission to view Executive Pulse. Ask IT leadership to enable access for your account." />
            </div>
        );
    }

    if (loading && !data) {
        return wrapPage(
            <div className="exec-pulse-dashboard exec-pulse-dashboard--message">
                <LoadingSpinner />
            </div>
        );
    }

    if (errorMessage && !data) {
        return wrapPage(
            <div className="exec-pulse-dashboard exec-pulse-dashboard--message">
                <ErrorMessage message={errorMessage} />
                <button
                    type="button"
                    className="exec-pulse-retry-btn"
                    onClick={reload}>
                    Try again
                </button>
            </div>
        );
    }

    return wrapPage(
        <div className="exec-pulse-dashboard exec-pulse-dashboard--fill">
            <div className="exec-pulse-dashboard__inner">
                <DashboardHeader
                    hub={data?.meta?.hub}
                    reportingPeriod={reportingPeriod}>
                    <ReportingPeriodSelector
                        year={year}
                        onChange={onYearChange}
                    />
                </DashboardHeader>

                <div className="exec-pulse-body">
                    <Tabs active={active} setActive={setActive} />

                    <div className="exec-pulse-body__scroll">
                        <SummaryStrip
                            items={items}
                            overallPercent={tabPercent}
                            reportingPeriod={reportingPeriod}
                        />

                        <SectionCard title="Key metrics">
                            <KPIGrid
                                items={items}
                                reportingPeriod={reportingPeriod}
                                onClick={(item) =>
                                    setSelectedKpiId(
                                        resolveExecutivePulseKpiId(item)
                                    )
                                }
                            />
                        </SectionCard>

                        <SectionCard title="Completion">
                            <Gauge value={overallPercent} />
                        </SectionCard>
                    </div>
                </div>
            </div>

            <DrillDownModal
                item={selectedKPI}
                onClose={() => setSelectedKpiId(null)}
                loggableKpiIds={
                    data?.meta?.loggableKpiIds ??
                    EXECUTIVE_PULSE_LOGGABLE_KPI_IDS
                }
                targetEditableKpiIds={
                    data?.meta?.targetEditableKpiIds ??
                    EXECUTIVE_PULSE_TARGET_EDITABLE_KPI_IDS
                }
                onDataChanged={refresh}
                reportingPeriod={reportingPeriod}
                reportingYear={year}
            />
        </div>
    );
}

ExecutiveDashboard.propTypes = {
    portalMode: PropTypes.bool
};

ExecutiveDashboard.defaultProps = {
    portalMode: false
};
